import { Person } from '@mui/icons-material';
import { Avatar, AvatarProps, Badge, styled, useTheme } from '@mui/material';
import { ParticipationBadge } from '@provision/chat-utils';
import { useMemo, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { pickAvatarColor } from 'src/utils/strings';

export const StyledAvatar = styled(Avatar, {
  name: 'StyledAvatar',
  shouldForwardProp: (prop) => prop !== 'backgroundColor',
})<{
  backgroundColor: string;
}>(({ backgroundColor, theme }) => ({
  position: 'relative',
  width: '2rem',
  height: '2rem',
  backgroundColor,
  color: theme.palette.custom.OV.sys.surfaceContainerLowest,
  fontSize: '0.5rem',
  fontWeight: 600,
  lineHeight: 1,
  letterSpacing: '0.01em',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  /*
    Simple solution to fit avatars of 1-4 symbols
    into a circle gracefully.
  */
  ['&[data-length="1"], &[data-length="2"]']: {
    fontSize: '0.85rem',
  },
  ['&[data-length="3"]']: {
    fontSize: '0.7rem',
  },
  ['&[data-length="4"]']: {
    fontSize: '0.6rem',
  },
}));

const LightingOverlay = styled('span')({
  position: 'absolute',
  inset: 0,
  borderRadius: 'inherit',
  backgroundImage:
    'linear-gradient(135deg, rgba(255, 255, 255, 0.14) 0%, rgba(0, 0, 0, 0.10) 100%)',
  boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.18), inset 0 -1px 2px rgba(0, 0, 0, 0.12)',
  pointerEvents: 'none',
});

const ImageLighting = styled('span')({
  position: 'absolute',
  inset: 0,
  borderRadius: 'inherit',
  backgroundImage:
    'linear-gradient(135deg, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0.05) 100%)',
  boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.09), inset 0 -1px 2px rgba(0, 0, 0, 0.06)',
  pointerEvents: 'none',
});

const ImageContainer = styled('span')({
  position: 'absolute',
  inset: 0,
  borderRadius: 'inherit',
  overflow: 'hidden',
  pointerEvents: 'none',
});

const PlaceholderPerson = styled(Person)({
  width: '75%',
  height: '75%',
});

const StyledOverlayImage = styled('img')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: 'inherit',
  pointerEvents: 'none',
});

const OverlayImage = ({ src }: { src: string }) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  if (errored) {
    return null;
  }

  return (
    <ImageContainer>
      <StyledOverlayImage
        src={src}
        alt=""
        aria-hidden
        onLoad={() => setLoaded(true)}
        onError={() => setErrored(true)}
      />
      {loaded ? <ImageLighting aria-hidden /> : null}
    </ImageContainer>
  );
};

const StyledBadgeDynamicIcon = styled(DynamicIcon, {
  shouldForwardProp: (prop) => prop !== 'backgroundColor',
  name: 'StyledBadgeDynamicIcon',
})<{
  backgroundColor: string;
}>(({ backgroundColor }) => ({
  fontSize: '1rem',
  width: '1rem',
  height: '1rem',
  borderRadius: '50%',
  backgroundColor,
  backgroundImage:
    'linear-gradient(135deg, rgba(255, 255, 255, 0.18) 0%, rgba(0, 0, 0, 0.10) 100%)',
  boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.20), inset 0 -1px 2px rgba(0, 0, 0, 0.12)',
}));

// green ring wrapper for badge icon
const OnlineRing = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  borderRadius: '50%',
  border: `2px solid ${theme.palette.custom.OV.sys.success}`,
  boxShadow: `inset 0 0 0 2px ${theme.palette.background.paper}`,
}));

StyledBadgeDynamicIcon.displayName = 'StyledBadgeDynamicIcon';

export type ColoredAvatarProps = {
  name: string;
  imageUrl?: string;
  badgeConfig?: ParticipationBadge;
  online?: boolean;
} & Omit<AvatarProps, 'src'>;

export const ColoredAvatar = ({
  name,
  imageUrl,
  badgeConfig,
  color,
  online,
  ...rest
}: ColoredAvatarProps) => {
  const theme = useTheme();
  const { shortName, backgroundColor } = useMemo(() => {
    return {
      shortName: getAbbr(name),
      backgroundColor: color || pickAvatarColor(name),
    };
  }, [name, color]);

  const avatarContent = (
    <StyledAvatar
      alt={name}
      backgroundColor={backgroundColor}
      data-length={shortName.length}
      {...rest}
    >
      {shortName ? shortName : <PlaceholderPerson />}
      <LightingOverlay aria-hidden />
      {imageUrl ? <OverlayImage key={imageUrl} src={imageUrl} /> : null}
    </StyledAvatar>
  );

  if (!badgeConfig) {
    return avatarContent;
  }

  return (
    <Badge
      sx={{ order: 0, zIndex: 0 }}
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={
        online ? (
          <OnlineRing>
            <StyledBadgeDynamicIcon
              color={theme.palette.custom.OV.sys.onTertiaryContainer}
              backgroundColor={theme.palette.custom.OV.sys.tertiaryContainer}
              name={badgeConfig.icon}
            />
          </OnlineRing>
        ) : (
          <StyledBadgeDynamicIcon
            color={badgeConfig.iconColor}
            backgroundColor={badgeConfig.backgroundColor}
            name={badgeConfig.icon}
          />
        )
      }
    >
      {avatarContent}
    </Badge>
  );
};

ColoredAvatar.displayName = 'ColoredAvatar';

function getAbbr(value: string): string {
  return value
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}
